import axios from 'axios';
import { defineStore } from 'pinia';

import { useAuthStore } from '~/store/auth';
import { CLAIM_MEDIA_TYPES } from '~/support/constants';

type MediaType = ValueOf<typeof CLAIM_MEDIA_TYPES>;

export const useMediaStore = defineStore('media', () => {
  const config = useRuntimeConfig();
  const auth = useAuthStore();
  const client = axios.create({
    baseURL: `${config.public.apiBaseUrl}/v1/media/upload`,
  });

  // Without the bearer, /v1/media/upload 403s and callers fall back to inlining
  // the file as a base64 data URL — which then 413s downstream on large images.
  client.interceptors.request.use(request => {
    if (!request.headers.Authorization) {
      request.headers.Authorization = `Bearer ${auth.accessToken}`;
    }

    return request;
  });

  async function put(file: File, meta: { signedUrl: string; publicUrl: string }) {
    const response = await fetch(meta.signedUrl, {
      body: file,
      headers: {
        'Content-Type': file.type,
      },
      method: 'PUT',
    });

    if (!response.ok) {
      throw new Error('Failed to upload file');
    }

    return meta.publicUrl;
  }

  async function sign(type: MediaType, file: File) {
    try {
      const { data } = await client.post('/', {
        contentType: file.type,
        filename: file.name,
        mediaType: type,
      });

      return data;
    } catch (error) {
      console.error('Failed to get signed URL:', error);

      // Return null to trigger data URL fallback
      return null;
    }
  }

  async function upload(file: File, type: MediaType) {
    const meta = await sign(type, file);

    // Fallback: use data URL when signed URL endpoint is unavailable
    if (!meta) {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          reject(new Error('Failed to read file'));
        };
        reader.readAsDataURL(file);
      });
    }

    return put(file, meta);
  }

  async function uploadMultiple(files: File[], type: MediaType) {
    return Promise.all(files.map(file => upload(file, type)));
  }

  return {
    upload,
    uploadMultiple,
  };
});
